import { useState, useMemo } from 'react';
import useScrollReveal from '../useScrollReveal';
import useLeadSubmit, { Honeypot, LeadError } from '../useLeadSubmit';
import { sectionStyles, getCardClass, isCardTransparent, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function BookingSection({ block = {}, brand, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {}, currentPage }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'calendar';

    const services = content.services || [];
    const timeSlots = content.time_slots || [];
    const availableDays = content.available_days || [1, 2, 3, 4, 5]; // Mon-Fri default
    const formFields = content.form_fields || [];

    const [step, setStep] = useState(services.length > 0 ? 0 : 1);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [formData, setFormData] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const cardBg = s.dark ? 'rgba(255,255,255,0.06)' : '#ffffff';
    const cardBorder = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';
    const inputBg = s.dark ? 'rgba(255,255,255,0.08)' : '#f9fafb';
    const inputBorder = s.dark ? 'rgba(255,255,255,0.15)' : '#d1d5db';

    const DAY_NAMES = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
    const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const calendarDays = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        let startDow = firstDay.getDay(); // 0=Sun
        startDow = startDow === 0 ? 6 : startDow - 1; // convert to 0=Mon

        const days = [];
        // pad with empty
        for (let i = 0; i < startDow; i++) days.push(null);
        for (let d = 1; d <= lastDay.getDate(); d++) {
            const date = new Date(year, month, d);
            let dow = date.getDay(); // 0=Sun
            dow = dow === 0 ? 6 : dow - 1; // 0=Mon
            const isPast = date < today;
            const isAvailable = availableDays.includes(dow) && !isPast;
            days.push({ date, day: d, isAvailable });
        }
        return days;
    }, [currentMonth, availableDays]);

    const prevMonth = () => {
        const d = new Date(currentMonth);
        d.setMonth(d.getMonth() - 1);
        if (d.getFullYear() >= today.getFullYear() || d.getMonth() >= today.getMonth()) {
            setCurrentMonth(d);
        }
    };

    const nextMonth = () => {
        const d = new Date(currentMonth);
        d.setMonth(d.getMonth() + 1);
        setCurrentMonth(d);
    };

    const isSameDate = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const leadSubmitHook = useLeadSubmit({
        brandId: brand?.id,
        blockType: 'booking',
        blockId: block.id,
        pageId: currentPage?.id || null,
        source: 'Reserva',
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const svc = selectedService != null ? services[selectedService]?.name : '';
        const dateStr = selectedDate ? `${selectedDate.getDate()}/${selectedDate.getMonth() + 1}/${selectedDate.getFullYear()}` : '';
        const leadData = { ...formData, servicio: svc, fecha: dateStr, hora: selectedTime };
        leadSubmitHook.submit(leadData);

        const cta = content.cta_link || '';
        if (cta.includes('wa.me') || cta.toLowerCase().includes('whatsapp')) {
            const fields = Object.entries(formData).map(([k, v]) => `${k}: ${v}`).join('\n');
            const msg = encodeURIComponent(`Reserva:\nServicio: ${svc}\nFecha: ${dateStr}\nHora: ${selectedTime}\n${fields}`);
            const url = cta.includes('?') ? `${cta}&text=${msg}` : `${cta}?text=${msg}`;
            window.open(url, '_blank');
        }
        setSubmitted(true);
    };

    const stepCount = services.length > 0 ? 4 : 3;
    const effectiveStep = services.length > 0 ? step : step - 1;
    const stepLabels = services.length > 0
        ? ['Servicio', 'Fecha', 'Hora', 'Datos']
        : ['Fecha', 'Hora', 'Datos'];

    const StepIndicator = () => (
        <div className="flex items-center justify-center gap-2 mb-8">
            {stepLabels.map((label, i) => {
                const isActive = i === effectiveStep;
                const isDone = i < effectiveStep;
                return (
                    <div key={i} className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${isActive || isDone ? 'text-white' : ''}`}
                            style={{
                                backgroundColor: isActive || isDone ? s.accent : (s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'),
                                color: isActive || isDone ? '#fff' : s.textSub,
                            }}>
                            {isDone ? (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            ) : i + 1}
                        </div>
                        <span className="text-xs font-medium hidden sm:block" style={{ color: isActive ? s.accent : s.textSub }}>{label}</span>
                        {i < stepLabels.length - 1 && (
                            <div className="w-8 h-px" style={{ backgroundColor: isDone ? s.accent : (s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb') }} />
                        )}
                    </div>
                );
            })}
        </div>
    );

    const SuccessMessage = () => (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <div className="max-w-lg mx-auto text-center py-12">
                    <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                        <svg className="w-8 h-8" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-bold mb-3" style={{ color: s.text }}>{content.success_message || 'Reserva confirmada'}</h3>
                    <p className="text-sm" style={{ color: s.textSub }}>Te contactaremos pronto para confirmar los detalles.</p>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );

    if (submitted) return <SuccessMessage />;

    // ── Calendar embed variant: external calendar (Calendly, Cal.com, etc.) ──
    const calendarUrl = config.calendar_url;
    if (calendarUrl) {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="max-w-4xl mx-auto">
                        <iframe
                            src={calendarUrl}
                            title="Calendario de reservas"
                            width="100%"
                            style={{ border: 'none', minHeight: '650px' }}
                            className="rounded-xl sm:min-h-[650px] min-h-[550px]"
                            loading="lazy"
                            allow="payment"
                        />
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Simple variant: direct form without calendar/steps ──
    if (variant === 'simple') {
        const handleSimpleSubmit = (e) => {
            e.preventDefault();
            leadSubmitHook.submit(formData);
            setSubmitted(true);
        };

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="max-w-xl mx-auto">
                        <form onSubmit={handleSimpleSubmit} className="rounded-2xl border p-6 md:p-8 space-y-5" style={{ backgroundColor: cardBg, borderColor: cardBorder, position: 'relative' }}>
                            <Honeypot onFill={leadSubmitHook.setHoneypot} />
                            {services.length > 0 && (
                                <div>
                                    <label className="text-sm font-medium mb-1.5 block" style={{ color: s.text }}>Servicio</label>
                                    <select
                                        value={formData.servicio || ''}
                                        onChange={e => setFormData(prev => ({ ...prev, servicio: e.target.value }))}
                                        className="w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none"
                                        style={{ backgroundColor: inputBg, borderColor: inputBorder, color: s.text }}>
                                        <option value="">Seleccionar...</option>
                                        {services.map((svc, i) => (
                                            <option key={i} value={svc.name}>{svc.name}{svc.price ? ` — ${svc.price}` : ''}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium mb-1.5 block" style={{ color: s.text }}>Fecha preferida</label>
                                    <input type="date" value={formData.fecha || ''} onChange={e => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
                                        className="w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none"
                                        style={{ backgroundColor: inputBg, borderColor: inputBorder, color: s.text }} />
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-1.5 block" style={{ color: s.text }}>Hora preferida</label>
                                    <select value={formData.hora || ''} onChange={e => setFormData(prev => ({ ...prev, hora: e.target.value }))}
                                        className="w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none"
                                        style={{ backgroundColor: inputBg, borderColor: inputBorder, color: s.text }}>
                                        <option value="">Seleccionar...</option>
                                        {(timeSlots.length > 0 ? timeSlots : ['09:00','10:00','11:00','12:00','14:00','15:00','16:00','17:00']).map((t, i) => (
                                            <option key={i} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {formFields.map((field, i) => (
                                <div key={i}>
                                    <label className="text-sm font-medium mb-1.5 block" style={{ color: s.text }}>{field.label}</label>
                                    <input type={field.type || 'text'} required value={formData[field.label] || ''}
                                        onChange={e => setFormData(prev => ({ ...prev, [field.label]: e.target.value }))}
                                        className="w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none"
                                        style={{ backgroundColor: inputBg, borderColor: inputBorder, color: s.text }}
                                        placeholder={field.label} />
                                </div>
                            ))}
                            <button type="submit"
                                className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-all hover:scale-[1.02] hover:shadow-lg"
                                style={{ backgroundColor: s.accent }}>
                                {content.submit_label || 'Solicitar reserva'}
                            </button>
                            <LeadError error={leadSubmitHook.error} />
                        </form>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Calendar variant (default): multi-step with calendar picker ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                <div className="max-w-2xl mx-auto">
                    <StepIndicator />

                    <div className="rounded-2xl border p-6 md:p-8" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>

                        {/* Step 0: Service selection */}
                        {step === 0 && services.length > 0 && (
                            <div>
                                <h3 className="text-lg font-bold mb-4" style={{ color: s.text }}>Selecciona un servicio</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {services.map((svc, i) => {
                                        const isSelected = selectedService === i;
                                        return (
                                            <button
                                                key={i}
                                                onClick={() => { setSelectedService(i); setStep(1); }}
                                                className={`p-4 rounded-xl border-2 text-left transition-all duration-300 hover:shadow-md ${isSelected ? 'shadow-md' : ''}`}
                                                style={{
                                                    backgroundColor: isSelected ? hexToRgba(s.accent, s.dark ? 0.15 : 0.05) : 'transparent',
                                                    borderColor: isSelected ? s.accent : (s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'),
                                                }}
                                            >
                                                <div className="font-semibold text-sm" style={{ color: s.text }}>{svc.name}</div>
                                                <div className="flex items-center gap-3 mt-1">
                                                    {svc.duration && (
                                                        <span className="text-xs flex items-center gap-1" style={{ color: s.textSub }}>
                                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                            {svc.duration}
                                                        </span>
                                                    )}
                                                    {svc.price && (
                                                        <span className="text-xs font-bold" style={{ color: s.accent }}>{svc.price}</span>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Step 1: Date picker */}
                        {step === 1 && (
                            <div>
                                <h3 className="text-lg font-bold mb-4" style={{ color: s.text }}>Selecciona una fecha</h3>
                                <div className="rounded-xl border p-4" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }}>
                                    {/* Month navigation */}
                                    <div className="flex items-center justify-between mb-4">
                                        <button onClick={prevMonth} className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:opacity-70"
                                            style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6' }}>
                                            <svg className="w-4 h-4" style={{ color: s.text }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                        </button>
                                        <span className="text-sm font-bold" style={{ color: s.text }}>
                                            {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                                        </span>
                                        <button onClick={nextMonth} className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:opacity-70"
                                            style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6' }}>
                                            <svg className="w-4 h-4" style={{ color: s.text }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    </div>
                                    {/* Day names */}
                                    <div className="grid grid-cols-7 gap-1 mb-2">
                                        {DAY_NAMES.map(d => (
                                            <div key={d} className="text-center text-xs font-medium py-1" style={{ color: s.textSub }}>{d}</div>
                                        ))}
                                    </div>
                                    {/* Days grid */}
                                    <div className="grid grid-cols-7 gap-1">
                                        {calendarDays.map((d, i) => {
                                            if (!d) return <div key={i} />;
                                            const isSelected = isSameDate(selectedDate, d.date);
                                            return (
                                                <button
                                                    key={i}
                                                    disabled={!d.isAvailable}
                                                    onClick={() => { setSelectedDate(d.date); setStep(2); }}
                                                    className={`w-full aspect-square rounded-lg text-xs font-medium transition-all duration-200 ${d.isAvailable ? 'hover:scale-110 cursor-pointer' : 'opacity-25 cursor-not-allowed'}`}
                                                    style={{
                                                        backgroundColor: isSelected ? s.accent : 'transparent',
                                                        color: isSelected ? '#fff' : s.text,
                                                    }}
                                                >
                                                    {d.day}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                {step > 0 && (
                                    <button onClick={() => setStep(step - 1)} className="mt-4 text-sm font-medium flex items-center gap-1 hover:opacity-70 transition-opacity" style={{ color: s.textSub }}>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                        Anterior
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Step 2: Time slots */}
                        {step === 2 && (
                            <div>
                                <h3 className="text-lg font-bold mb-1" style={{ color: s.text }}>Selecciona una hora</h3>
                                {selectedDate && (
                                    <p className="text-sm mb-4" style={{ color: s.textSub }}>
                                        {selectedDate.getDate()} de {MONTH_NAMES[selectedDate.getMonth()]}
                                    </p>
                                )}
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                    {timeSlots.map((time, i) => {
                                        const isSelected = selectedTime === time;
                                        return (
                                            <button
                                                key={i}
                                                onClick={() => { setSelectedTime(time); setStep(3); }}
                                                className={`py-2.5 px-3 rounded-xl text-sm font-medium border transition-all duration-200 hover:scale-105 ${isSelected ? 'shadow-md' : ''}`}
                                                style={{
                                                    backgroundColor: isSelected ? s.accent : 'transparent',
                                                    color: isSelected ? '#fff' : s.text,
                                                    borderColor: isSelected ? s.accent : (s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'),
                                                }}
                                            >
                                                {time}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button onClick={() => setStep(1)} className="mt-4 text-sm font-medium flex items-center gap-1 hover:opacity-70 transition-opacity" style={{ color: s.textSub }}>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                    Anterior
                                </button>
                            </div>
                        )}

                        {/* Step 3: Contact form */}
                        {step === 3 && (
                            <form onSubmit={handleSubmit} style={{position:'relative'}}>
                                <Honeypot onFill={leadSubmitHook.setHoneypot} />
                                <h3 className="text-lg font-bold mb-4" style={{ color: s.text }}>Tus datos</h3>
                                {/* Summary */}
                                <div className="rounded-xl p-4 mb-6 space-y-1" style={{ backgroundColor: hexToRgba(s.accent, s.dark ? 0.1 : 0.04) }}>
                                    {selectedService !== null && services[selectedService] && (
                                        <div className="flex items-center gap-2 text-xs" style={{ color: s.text }}>
                                            <svg className="w-3.5 h-3.5 shrink-0" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                            <span className="font-medium">{services[selectedService].name}</span>
                                        </div>
                                    )}
                                    {selectedDate && (
                                        <div className="flex items-center gap-2 text-xs" style={{ color: s.text }}>
                                            <svg className="w-3.5 h-3.5 shrink-0" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                            <span className="font-medium">{selectedDate.getDate()} de {MONTH_NAMES[selectedDate.getMonth()]}</span>
                                        </div>
                                    )}
                                    {selectedTime && (
                                        <div className="flex items-center gap-2 text-xs" style={{ color: s.text }}>
                                            <svg className="w-3.5 h-3.5 shrink-0" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                            <span className="font-medium">{selectedTime}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-4">
                                    {formFields.map((field, i) => (
                                        <div key={i}>
                                            <label className="text-sm font-medium mb-1.5 block" style={{ color: s.text }}>{field.label}</label>
                                            <input
                                                type={field.type || 'text'}
                                                required
                                                value={formData[field.label] || ''}
                                                onChange={e => setFormData(prev => ({ ...prev, [field.label]: e.target.value }))}
                                                className="w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none"
                                                style={{ backgroundColor: inputBg, borderColor: inputBorder, color: s.text, outlineColor: s.accent }}
                                                placeholder={field.label}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center gap-3 mt-6">
                                    <button type="button" onClick={() => setStep(2)} className="px-5 py-2.5 rounded-xl text-sm font-medium border transition-all hover:opacity-80"
                                        style={{ borderColor: s.dark ? 'rgba(255,255,255,0.15)' : '#d1d5db', color: s.textSub }}>
                                        Anterior
                                    </button>
                                    <button type="submit"
                                        className="flex-1 py-3 rounded-xl font-semibold text-sm text-white transition-all hover:scale-[1.02] hover:shadow-lg"
                                        style={{ backgroundColor: s.accent }}>
                                        {content.submit_label || 'Confirmar reserva'}
                                    </button>
                                </div>
                            </form>
                        )}
                        <LeadError error={leadSubmitHook.error} />
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
